import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Tooltip,
} from "antd";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import { noTrailingSpaceAllowedRule } from "utilities/generalUtility";
import UDF from "redux/models/udf";
import { isEmpty } from "lodash";
import AgGridUtility from "../../../../../utilities/agGridUtility";
import { EditLayoutButton } from "./editLayoutButton/editLayoutButton";
import { DataTableLayout } from "../../../../../common/components/dataTableLayout/dataTableLayout";
import { usePageFilter } from "../../../../../hooks/usePageFilters";
import { ClientSideDataGrid } from "../../../../../common/components/clientSideDataGrid/clientSideDataGrid";
import { getColumns } from "./columns";

export default function UdfField({
  entityId,
  tableConfigKey,
  layoutConfigKey = null,
  layoutDisplayName = null,
  isLayoutEditable = false,
  getDefaultFieldLayout = () => [],
  previewFieldRenderer = (field) => field.label,
}) {
  const userId = useSelector(selectUserId);
  const userSlice = useSelector(selectUser);

  const [form] = Form.useForm();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [udfs, setUdfs] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [search, setSearch] = usePageFilter(
    `client_${id}_udf_${entityId}_search`,
  );

  const layoutFields = getDefaultFieldLayout(udfs);
  const isEditLayoutButtonDisabled = isEmpty(udfs);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const editRecord = (record) => {
    const options = record?.udfMultiValueFieldTypeVal.map((item) => ({
      value: item.value,
      id: item.id,
    }));

    const data = {
      ...record,
      options,
      isVisible: record.isVisible === 1,
      isExportable: record.isExportable === 1,
    };

    setEditData(data);
    showModal();
  };
  const afterModalClose = () => {
    setEditData({});
  };

  const onFinish = async (values) => {
    const { options, isVisible, isExportable, isRequired, label, ...rest } =
      values;
    const payload = {
      ...rest,
      label,
      entityId,
      isVisible: isVisible ? 1 : 0,
      isExportable: isExportable ? 1 : 0,
      isRequired: isRequired ? 1 : 0,
      multiFieldValues:
        values?.options?.filter((item) => item.value != null) ?? [],
      clientId: id,
    };
    setIsLoading(true);
    if (Object.keys(editData).length) {
      try {
        values = { ...payload, udfId: editData.id };
        await UDF.updateUdf(values);
        getAllUDFBYEntityID();
        setIsLoading(false);
        setIsModalVisible(false);
        form.resetFields();
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
      setEditData({});
    } else {
      try {
        await UDF.createUdf({
          udfs: [
            {
              ...payload,
            },
          ],
        });
        getAllUDFBYEntityID();
        setIsLoading(false);
        setIsModalVisible(false);
        form.resetFields();
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    }
  };

  const getAllUDFBYEntityID = async () => {
    try {
      const body = { clientId: id, entityId };
      const res = await UDF.getUdfList(body);
      setUdfs(res);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [editData]);

  useEffect(() => {
    getAllUDFBYEntityID();
  }, []);
  return (
    <>
      <DataTableLayout
        searchDefaultValue={search}
        onSearchChange={setSearch}
        actions={[
          {
            permissions: [K.Permissions.UDFCreate],
            renderer: () => (
              <Col>
                <Button type="primary" onClick={showModal}>
                  Add New Field
                </Button>
              </Col>
            ),
          },
          {
            hide: !isLayoutEditable,
            permissions: [K.Permissions.UDFEdit],
            renderer: () => (
              <Col>
                <Tooltip
                  title={
                    isEditLayoutButtonDisabled
                      ? "You need at least one field to edit layout"
                      : undefined
                  }
                >
                  <span>
                    <EditLayoutButton
                      layoutConfigKey={layoutConfigKey}
                      layoutDisplayName={layoutDisplayName}
                      fields={layoutFields}
                      fieldRenderer={previewFieldRenderer}
                      onSuccess={() => {}}
                      disabled={isEditLayoutButtonDisabled}
                    />
                  </span>
                </Tooltip>
              </Col>
            ),
          },
        ]}
      >
        <ClientSideDataGrid
          userId={userId}
          configKey={tableConfigKey}
          defaultColumnConfig={getColumns(userSlice.roles, editRecord)}
          rowData={udfs}
          quickFilterText={search}
          getContextMenuItems={() => AgGridUtility.menuItemsWithExport}
        />
      </DataTableLayout>
      <Modal
        forceRender
        className="s2-theme-style"
        title={`${Object.keys(editData).length ? "Edit" : "Create"} UDF`}
        open={isModalVisible}
        centered
        closeIcon={<i className="icon-closeable" />}
        okText={`${Object.keys(editData).length ? "Update" : "Add"}`}
        wrapClassName="vertical-center-modal"
        onOk={handleOk}
        okButtonProps={{ loading: isLoading }}
        onCancel={handleCancel}
        afterClose={afterModalClose}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={editData}
        >
          <Form.Item
            label="Label"
            name="label"
            rules={[
              { required: true, message: "Name is required." },
              noTrailingSpaceAllowedRule(),
            ]}
          >
            <Input placeholder="Title" />
          </Form.Item>

          <Form.Item
            label="Select Field type "
            name="fieldTypeId"
            rules={[
              { required: true, message: "Field Type is required." },
              noTrailingSpaceAllowedRule(),
            ]}
          >
            <Select
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              showArrow
              disabled={Object.keys(editData).length}
              showSearch
              mode="single"
              optionFilterProp="label"
              placeholder="Select Type"
              className="billingTypeTitle"
              options={Object.keys(K.UDF.Fields).map((fieldKey) => ({
                value: +fieldKey,
                label: K.UDF.Fields[fieldKey].displayName,
              }))}
            />
          </Form.Item>

          <Form.Item shouldUpdate className="mb-0">
            {({ getFieldValue }) => {
              const Component =
                K.UDF.Fields[getFieldValue("fieldTypeId")]?.component;
              const componentProps =
                K.UDF.Fields[getFieldValue("fieldTypeId")]?.props;

              return (
                Component && (
                  <Component
                    props={componentProps}
                    isFormItem
                    disabled={Object.keys(editData).length}
                    form={form}
                    title={getFieldValue("label")}
                    placeholder={getFieldValue("label")}
                    formItemName={`${getFieldValue(
                      "label",
                    )}.replace(/\s/g, '') `}
                  />
                )
              );
            }}
          </Form.Item>

          <Row>
            <Col xs={24} md={8} xl={8}>
              <Form.Item name="isVisible" valuePropName="checked">
                <Checkbox>Is Visible</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={8} xl={8}>
              <Form.Item name="isExportable" valuePropName="checked">
                <Checkbox>Is Exportable</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={8} xl={8}>
              <Form.Item name="isRequired" valuePropName="checked">
                <Checkbox>Is Required</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal className="s2-theme-style" title="Edit field">
        <Form>
          <Form.Item label="Label">
            <Input placeholder="Name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
